import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {
  AllergenGroup,
  AllergenGroupOverride,
  AllergenGroupRequestDto,
  emptyOverride,
  NameTranslationType,
  OverrideInstruction,
  OverridePositionRequest,
  OverridePositionsMap,
  PositionTypeEnum,
} from "app/models/menu.model";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {Organization} from "app/models/organization.model";
import {Store} from "@ngrx/store";
import {BaseModalComponent} from "app/shared/base-modal/base-modal.component";
import {AllergenGroupService} from "app/services/allergen-group.service";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {MenuOverrideService} from "app/services/menu-override.service";
import {MenuService} from "app/services/menu.service";
import {TranslateService} from "@ngx-translate/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
  selector: 'app-edit-allergen-group-modal',
  templateUrl: './edit-allergen-group-modal.component.html',
  styleUrls: ['./edit-allergen-group-modal.component.scss']
})
export class EditAllergenGroupModalComponent extends BaseModalComponent implements OnInit{
  initValues = {}
  form: FormGroup;
  overridePositionsMap: OverridePositionsMap;
  selectedAllergenGroupImgSrc: string;
  allergenGroupNames: Set<string> = new Set();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private translate: TranslateService,
    private menuService: MenuService,
    private allergenGroupService: AllergenGroupService,
    public dialogRef: MatDialogRef<EditAllergenGroupModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditAllergenGroupModalComponentDataType
  ) {
    super(dialogRef);
    for (const allergenGroup of this.data.allergenGroups) {
      this.allergenGroupNames.add(allergenGroup.name);
    }
    this.initValues["allergenGroupName"] = data.allergenGroup.name;
    this.initValues['picture'] = this.data.allergenGroup.externalPictureUrl ?? '';
    this.initValues['textColor'] = this.data.allergenGroup.textColor ?? '';
    this.initValues['backgroundColor'] = this.data.allergenGroup.backgroundColor ?? '';

    this.form = new FormGroup({});

    this.form.addControl('textColor', new FormControl(data.allergenGroup.textColor ?? ''));
    this.form.addControl('backgroundColor', new FormControl(data.allergenGroup.backgroundColor ?? ''));
    this.form.addControl('picture', new FormControl(data.allergenGroup.externalPictureUrl ?? ''));
    this.form.addControl('allergenGroupName', new FormControl(data.allergenGroup.name ?? ''));
    this.form.addControl('isManuallyCreated', new FormControl({value: this.isManuallyCreated(), disabled: true}));

    if(!data.allergenGroup.nameTranslations){
      data.allergenGroup.nameTranslations = {} as NameTranslationType;
    }

    for(const lang of this.data.organization.additionalLanguages){
      this.initValues[lang] = data.allergenGroup.nameTranslations[lang] ?? null;
      this.form.addControl(lang, new FormControl(data.allergenGroup.nameTranslations[lang] ?? ''))
    }
    this.selectedAllergenGroupImgSrc = this.data.allergenGroup.externalPictureUrl ?? '';
  }

  ngOnInit(): void {
    if (!this.isManuallyCreated()) {
      this.store.select(MenuSelectors.selectOverridePositionsMap)
        .pipe(untilDestroyed(this))
        .subscribe(overridePositionsMap => {
          this.overridePositionsMap = overridePositionsMap;
        });
    }
  }

  async submit() {
    if(!this.validate()){
      return;
    }
    const translations: NameTranslationType = {};
    for (const lang of this.data.organization.additionalLanguages) {
      translations[ lang ] = this.form.value[ lang ];
    }
    const requestDto = {
      name: this.form.value[ 'allergenGroupName' ],
      nameTranslations: translations,
      textColor: this.form.value[ 'textColor' ],
      backgroundColor: this.form.value[ 'backgroundColor' ],
    } as AllergenGroupRequestDto;

    if (this.isManuallyCreated()) {
      if (this.data.allergenGroupId == null) {
        const response = await this.allergenGroupService.create(this.data.menuId, requestDto);
        const result = {created: {...response, externalPictureUrl: this.form.get('picture').value}};
        await this.saveAllergenGroupOverrides(response?.id);
        this.exit(result);
      } else {
        const response = await this.allergenGroupService.update(this.data.allergenGroupId, requestDto);
        const result = {updated: {...response, externalPictureUrl: this.form.get('picture').value}};
        await this.saveAllergenGroupOverrides();
        this.exit(result);
      }
    } else {
      const result = {updated: {...this.data.allergenGroup, ...requestDto, externalPictureUrl: this.form.get('picture').value}};
      await this.saveAllergenGroupOverrides();
      this.exit(result);
    }
  }

  async saveAllergenGroupOverrides(allergenGroupId?: string) {
    let allergenGroupOverride: AllergenGroupOverride;
    let isOverrideExists: boolean;
    let overridePosition = this.overridePositionsMap?.getOverride(PositionTypeEnum.AllergenGroups, this.data.allergenGroupId ?? allergenGroupId);
    // if override position does not exists create a new one
    if (!overridePosition) {
      overridePosition = {
        positionType: PositionTypeEnum.AllergenGroups,
        positionId: this.data.allergenGroupId ?? allergenGroupId,
        override: emptyOverride,
        menuSectionId: this.data.menuSectionId,
      };
      allergenGroupOverride = {};
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      allergenGroupOverride = JSON.parse(overridePosition.override) as AllergenGroupOverride;
    }

    const overrideInstructions: OverrideInstruction[] = []

    if(this.data.originalAllergenGroup.externalId){
      if (this.initValues[ 'allergenGroupName' ].trim() !== this.form.value[ 'allergenGroupName' ].trim()) {
        MenuOverrideService.overrideTextFiled(this.data.allergenGroup, this.data.originalAllergenGroup, 'name', this.form.value[ "allergenGroupName" ], isOverrideExists, overrideInstructions);
      }
      if (this.initValues[ 'textColor' ].trim() !== this.form.value[ 'textColor' ].trim()) {
        MenuOverrideService.overrideTextFiled(this.data.allergenGroup, this.data.originalAllergenGroup, 'textColor', this.form.value[ "textColor" ], isOverrideExists, overrideInstructions);
      }
      if (this.initValues[ 'backgroundColor' ].trim() !== this.form.value[ 'backgroundColor' ].trim()) {
        MenuOverrideService.overrideTextFiled(this.data.allergenGroup, this.data.originalAllergenGroup, 'backgroundColor', this.form.value[ "backgroundColor" ], isOverrideExists, overrideInstructions);
      }

      if (this.data.organization.additionalLanguages) {
        for (const lang of this.data.organization.additionalLanguages) {
          if (this.initValues[ lang ] !== this.form.value[ lang ]) {
            MenuOverrideService.overrideTranslationFiled(this.data.allergenGroup, this.data.originalAllergenGroup, 'nameTranslations', lang, this.form.value[ lang ], isOverrideExists, allergenGroupOverride, overrideInstructions)
          }
        }
      }
    }

    if (this.initValues['picture'] !== this.form.get('picture').value) {
      MenuOverrideService.overrideTextFiled(this.data.allergenGroup, this.data.originalAllergenGroup, 'pictureUrl', this.selectedAllergenGroupImgSrc, isOverrideExists, overrideInstructions, true);
    }

    const menuSectionId = this.data.menuSectionId;
    if (overrideInstructions.length > 0) {

      const overridePositionRequest: OverridePositionRequest = {
        ...overridePosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(menuSectionId, [overridePositionRequest], [this.data.menuSectionId])
        .then(_=> _)
        .catch(error => alert(error.error));
    }
  }

  async remove() {
    if(confirm(this.translate.instant('MENU.ALLERGEN_GROUP.CONFIRM_DELETE'))){
      await this.allergenGroupService.delete(this.data.allergenGroupId);
      this.exit({deleted: this.data.allergenGroupId})
    }
  }

  isManuallyCreated(): boolean {
    return !this.data.allergenGroup.externalId;
  }

  restoreName() {
    this.form.get('allergenGroupName').setValue(this.data.originalAllergenGroup.name);
  }

  isNameDifferent() {
    return this.data.originalAllergenGroup && this.form.value.allergenGroupName !== this.data.originalAllergenGroup.name;
  }

  onImageChanged($event: string) {
    this.form.get('picture').setValue($event);
    this.selectedAllergenGroupImgSrc = $event;
  }

  validate() {
    const tagName = this.form.value[ 'allergenGroupName' ];
    if(!tagName){
      this.form.setErrors({'allergenGroupName': "Name is required"});
      return false;
    }
    if(this.allergenGroupNames.has(tagName)){
      this.form.setErrors({'allergenGroupName': "Name should be unique"});
      return false;
    }

    return true;
  }
}

export interface EditAllergenGroupModalComponentDataType {
  allergenGroupId: string | null,
  allergenGroup: AllergenGroup,
  originalAllergenGroup: AllergenGroup,
  organization: Organization,
  menuSectionId: string,
  menuId: string,
  allowOverrides: boolean,
  allergenGroups: AllergenGroup[],
}
